import { __awaiter } from 'tslib';
import { ɵɵdefineInjectable, ɵsetClassMetadata, Injectable, ɵɵinject, Inject, ɵɵdefineNgModule, ɵɵdefineInjector, NgModule } from '@angular/core';
import { HttpHeaders, HttpClient, HTTP_INTERCEPTORS } from '@angular/common/http';

/*!
 * American Well Converge
 *
 * Copyright © 2020 American Well Systems.
 * All rights reserved.
 *
 * It is illegal to use; reproduce or distribute
 * any part of this Intellectual Property without
 * prior written authorization from American Well.
 */
/**
* Performs various utility functionality
*/
class CoreUtils {
    /**
     * Determines if a value is of a null or undefined type and string.
     *
     * @param value the value to check if it is null or undefined
     * @returns true, if value is undefined or null. Otherwise, false.
     */
    static isNullOrUndefined(value) {
        return (value === null || value === undefined || value === 'undefined' || value === 'null');
    }
    /**
     * Determines if a string value is an empty string.
     *
     * @param value the value to check if it is an empty string
     * @returns true, if value is empty. Otherwise, false.
     */
    static isEmptyString(value) {
        return (value === null || value === undefined || value.trim() === '');
    }
    /**
     * Locates the endpoint URL using its name.
     *
     * @param endpoints the collection of endpoints
     * @param name the name of the endpoint to locate its URL
     *
     * @returns the endpoint url, if found. Otherwise, null.
     */
    static getEndpointUrl(endpoints, name) {
        for (let index = 0; index < endpoints.length; index++) {
            if (endpoints[index].name == name) {
                return endpoints[index].url;
            }
        }
        console.error('Could not find the endpoint url for', name);
        return null;
    }
}

/*!
 * American Well Converge
 *
 * Copyright © 2020 American Well.
 * All rights reserved.
 *
 * It is illegal to use, reproduce or distribute
 * any part of this Intellectual Property without
 * prior written authorization from American Well.
 */
class ServiceConfig {
    constructor() { }
}

/*!
 * American Well Converge
 *
 * Copyright © 2020 American Well.
 * All rights reserved.
 *
 * It is illegal to use, reproduce or distribute
 * any part of this Intellectual Property without
 * prior written authorization from American Well.
 */
function decode(string, options = { header: false }) {
    let base64Url = string.split('.')[1];
    if (options.header) {
        base64Url = string.split('.')[0];
    }
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(base64));
}
class TokenContext {
    constructor() { }
    /**
     * Extracts context from the token.
     *
     * @param token the token
     *
     * @returns the extracted token context or undefined if the token is empty
     */
    static parse(token) {
        if (CoreUtils.isEmptyString(token)) {
            return undefined;
        }
        const header = decode(token, { header: true });
        const payload = decode(token);
        return { header, payload };
    }
}

/**
 * The status used to initiate a new participant encounter.
 */
var ParticipantStatus;
(function (ParticipantStatus) {
    ParticipantStatus["NONE"] = "NONE";
    ParticipantStatus["NOT_CONNECTED"] = "NOT_CONNECTED";
    ParticipantStatus["INVITED_BY_HOST"] = "INVITED_BY_HOST";
})(ParticipantStatus || (ParticipantStatus = {}));

class SessionCacheService {
    constructor() { }
    /**
     * Saves the key value pair to session storage.
     *
     * @param key the item key
     * @param value the item value
     *
     * @returns undefined
     */
    static save(key, value) {
        if (typeof value === "string" || value instanceof String) {
            sessionStorage.setItem(key, value.toString());
        }
        else {
            sessionStorage.setItem(key, JSON.stringify(value));
        }
        console.debug(key, "has been persisted in client's session storage");
    }
    /**
     * Clears all session items
     *
     * @returns undefined
     */
    static clearAll() {
        sessionStorage.clear();
        console.debug("All items have been removed from client's session storage");
    }
    /**
     * Removes the item from the session
     *
     * @param key the item key
     *
     * @returns undefined
     */
    static remove(key) {
        sessionStorage.removeItem(key);
        console.debug(key, "has been removed from client's session storage");
    }
    /**
     * Gets the item's value from the session. Use for string type value
     *
     * @param key the item key
     *
     * @returns the item's value
     */
    static getString(key) {
        const value = sessionStorage.getItem(key);
        if (value && value !== 'undefined') {
            return value;
        }
        return null;
    }
    /**
     * Gets the item value from the session. Use for object type value
     *
     * @param key the item key
     *
     * @returns the item's value
     */
    static getObject(key) {
        const value = SessionCacheService.getString(key);
        return (value) ? JSON.parse(value) : null;
    }
}
SessionCacheService.ɵfac = function SessionCacheService_Factory(t) { return new (t || SessionCacheService)(); };
SessionCacheService.ɵprov = ɵɵdefineInjectable({ token: SessionCacheService, factory: SessionCacheService.ɵfac, providedIn: 'root' });
/*@__PURE__*/ (function () { ɵsetClassMetadata(SessionCacheService, [{
        type: Injectable,
        args: [{
                providedIn: 'root'
            }]
    }], function () { return []; }, null); })();

/*!
 * American Well Converge
 *
 * Copyright © 2020 American Well Systems.
 * All rights reserved.
 *
 * It is illegal to use; reproduce or distribute
 * any part of this Intellectual Property without
 * prior written authorization from American Well.
 */
class AppConstants {
}
AppConstants.AUTH_TOKEN_HEADER_NAME = 'Authorization';
AppConstants.AUTH_TOKEN_PREFIX = 'Bearer ';
AppConstants.SESSION_ID_HEADER_NAME = 'X-Client-Session-Id';
AppConstants.SESSION_ID_KEY = 'aw-session-id';
AppConstants.TOKEN_KEY = 'vms-token';
AppConstants.LITE_TOKEN_KEY = 'lite-token';
AppConstants.DISPLAY_NAME_KEY = 'displayName';
AppConstants.MIC_MUTE = 'MIC_MUTE';
AppConstants.MIC_UNMUTE = 'MIC_UNMUTE';
AppConstants.VIDEO_MUTE = 'VIDEO_MUTE';
AppConstants.VIDEO_UNMUTE = 'VIDEO_UNMUTE';
AppConstants.NORMAL_SCREEN = 'NORMAL_SCREEN';
AppConstants.FULL_SCREEN = 'FULL_SCREEN';
AppConstants.SHOW_PERMISSION = 'SHOW_PERMISSION_WARNING';
AppConstants.HIDE_PERMISSION = 'HIDE_PERMISSION_WARNING';
AppConstants.HIDE_SELF_VIEW = 'HIDE_SELF_VIEW';
AppConstants.SHOW_SELF_VIEW = 'SHOW_SELF_VIEW';
AppConstants.SPEAKER_CHANGED = 'SPEAKER_CHANGED';
AppConstants.CAMERA_CHANGED = 'CAMERA_CHANGED';
AppConstants.MIC_CHANGED = 'MIC_CHANGED';
AppConstants.API_URL_PARTICIPANT = '/api/v2/participant';
AppConstants.API_URL_GHOST = '/api/participant/ghost';
AppConstants.API_URL_VOICE_TOKEN = '/api/v1/voice/token';
AppConstants.ɵfac = function AppConstants_Factory(t) { return new (t || AppConstants)(); };
AppConstants.ɵprov = ɵɵdefineInjectable({ token: AppConstants, factory: AppConstants.ɵfac, providedIn: 'root' });
/*@__PURE__*/ (function () { ɵsetClassMetadata(AppConstants, [{
        type: Injectable,
        args: [{
                providedIn: 'root'
            }]
    }], null, null); })();

/*!
 * American Well Converge
 *
 * Copyright © 2021 American Well.
 * All rights reserved.
 *
 * It is illegal to use, reproduce or distribute
 * any part of this Intellectual Property without
 * prior written authorization from American Well.
 */
/*
 * Represents FHIR data for a participant
 */
class IndividualFhirData {
    /**
     * Instantiates an IndividualFhirData
     *
     * @param reference the reference
     * @param emailAddresses the email addresses
     * @param phoneNumbers the phone numbers
     * @param externalUserId the external user Id
     *
     * @returns the participant FHIR data
     */
    constructor(reference, emailAddresses, phoneNumbers, externalUserId) {
        this.reference = reference;
        this.emailAddresses = emailAddresses;
        this.phoneNumbers = phoneNumbers;
        this.externalUserId = externalUserId;
    }
}

class ParticipantRequest {
    /**
     * Instantiates an participantRequestData
     * @param url endpoint url
     * @param token the participant token
     * @param conferenceVendorId the conference vendor Id
     * @param displayName the participant display name
     * @param preferredPhone the participant preferred preferredPhone
     * @param patientId the CDR patient id or reference
     * @param providerId the CDR provider id or reference
     * @param encounterId the CDR encounter id or reference
     * @param initialParticipantStatus the participant status
     * @param encounterTenantKey the tenant key of the encounter. This takes priority over the tenant key in the token
     * @param proxySourceId optional data derived from the FHIR resource of the participant to display current patient's proxy's source id
     * @param proxyDisplayName optional data derived from the FHIR resource of the participant to display current patient's proxy person's name
     *
     * @returns the participant FHIR data
     */
    constructor(url, token, conferenceVendorId, displayName, preferredPhone, fhirData, initialParticipantStatus, userAgent, patientId, providerId, encounterId, encounterTenantKey, proxySourceId, proxyDisplayName) {
        this.url = url;
        this.token = token;
        this.conferenceVendorId = conferenceVendorId;
        this.displayName = displayName;
        this.preferredPhone = preferredPhone;
        this.fhirData = fhirData;
        this.userAgent = userAgent;
        this.patientId = patientId;
        this.providerId = providerId;
        this.encounterId = encounterId;
        this.initialParticipantStatus = initialParticipantStatus;
        this.encounterTenantKey = encounterTenantKey;
        this.proxySourceId = proxySourceId;
        this.proxyDisplayName = proxyDisplayName;
    }
}

class VideoManagementClientService {
    constructor(http, serviceConfig) {
        this.http = http;
        this.serviceConfig = serviceConfig;
        this.headers = new HttpHeaders({ 'Content-type': 'application/json' });
    }
    /**
     * Sets up a conference for a participant in the video management service. It creates
     * a participant if that participate hasn't been created.
     * @param token the participant token
     * @param conferenceVendorId the conference vendor Id
     * @param displayName the participant display name
     * @param preferredPhone the participant preferred preferredPhone
     * @param patientId the CDR patient id or reference
     * @param providerId the CDR provider id or reference
     * @param encounterId the CDR encounter id or reference
     * @param status the participant status
     * @param encounterTenantKey the tenant key of the encounter. This takes priority over the tenant key in the token
     * @param externalUserId data derived from the FHIR resource of the participant
     * @param proxySourceId optional data derived from the FHIR resource of the participant to display current patient's proxy's source id
     * @param proxyDisplayName optional data derived from the FHIR resource of the participant to display current patient's proxy person's name
     *
     * @returns the participant including the context details
     */
    setupEncounter(token, conferenceVendorId, displayName, preferredPhone, patientId, providerId, encounterId, status, encounterTenantKey, externalUserId, proxySourceId, proxyDisplayName) {
        return __awaiter(this, void 0, void 0, function* () {
            console.debug('Started setupEncounter');
            const url = CoreUtils.getEndpointUrl(this.serviceConfig.endpoints, 'createOrUpdateParticipant') + AppConstants.API_URL_PARTICIPANT;
            if (CoreUtils.isEmptyString(url)) {
                return Promise.reject(new Error('Internal Validation Error: No endpoint for createOrUpdateParticipant found'));
            }
            const liteTokenUrl = CoreUtils.getEndpointUrl(this.serviceConfig.endpoints, 'getLiteToken');
            if (CoreUtils.isEmptyString(liteTokenUrl)) {
                return Promise.reject(new Error('Internal Validation Error: No endpoint for liteToken found'));
            }
            SessionCacheService.save(AppConstants.TOKEN_KEY, token);
            const userAgent = navigator.userAgent;
            return new Promise((resolve, reject) => __awaiter(this, void 0, void 0, function* () {
                const fhirData = new IndividualFhirData(undefined, [], [], externalUserId);
                const participantRequest = new ParticipantRequest(url, token, conferenceVendorId, displayName, preferredPhone, fhirData, status, userAgent, patientId, providerId, encounterId, encounterTenantKey, proxySourceId, proxyDisplayName);
                yield this.sendParticipantRequest(participantRequest)
                    .then((encounterResponse) => __awaiter(this, void 0, void 0, function* () {
                    try {
                        const headers = new HttpHeaders({
                            Authorization: `Bearer ${token}`
                        });
                        const res = yield this.http.post(liteTokenUrl, {}, { headers }).toPromise();
                        if (res.token) {
                            SessionCacheService.save(AppConstants.LITE_TOKEN_KEY, res.token);
                        }
                        else {
                            return reject(new Error('Error occurred in getting the lite token. lite token is null.'));
                        }
                    }
                    catch (err) {
                        console.error('Error occurred in getting the lite token.');
                        return reject(err);
                    }
                    console.debug('Completed setupEncounter');
                    return resolve(encounterResponse);
                }))
                    .catch((errorResponse) => {
                    console.error('Error occurred setting up the encounter.');
                    return reject(errorResponse);
                });
            }));
        });
    }
    /**
     * Creates or updates a ghost participant with the specified details.
     *
     * @param token - the token for the participant
     * @param conferenceVendorId - the conference vendor id for the participant
     * @param displayName - the display name of the participant
     * @param fhirData - data derived from the FHIR resource of the participant to create
     * @param preferredPhone - the preferred phone number of the participant
     * @param status - the status to create or update the ghost participant with
     * @param patientId the CDR patient id or reference
     * @param providerId the CDR provider id or reference
     * @param encounterId the CDR encounter id or reference
     * @param encounterTenantKey the tenant key of the encounter. This takes priority over the tenant key in the token
     * @returns the participant that was created or updated including the context details
     */
    createOrUpdateGhostParticipant(token, conferenceVendorId, displayName, fhirData, preferredPhone, status = ParticipantStatus.NOT_CONNECTED, patientId, providerId, encounterId, encounterTenantKey) {
        return __awaiter(this, void 0, void 0, function* () {
            const url = CoreUtils.getEndpointUrl(this.serviceConfig.endpoints, 'createOrUpdateGhostParticipant') + AppConstants.API_URL_GHOST;
            if (CoreUtils.isEmptyString(url)) {
                return Promise.reject(new Error('Internal Validation Error: No endpoint for createOrUpdateGhostParticipant found'));
            }
            const participantRequest = new ParticipantRequest(url, token, conferenceVendorId, displayName, preferredPhone, fhirData, status, undefined, patientId, providerId, encounterId, encounterTenantKey);
            return this.sendParticipantRequest(participantRequest);
        });
    }
    /**
     * Sends a request to create or update a participant to the specified url.
     * @param token - the token for the participant
     * @param conferenceVendorId - the conference vendor id for the participant
     * @param displayName - the display name of the participant
     * @param preferredPhone - the preferred phone number of the participant
     * @param fhirData - data derived from the FHIR resource of the participant to create
     * @param initialParticipantStatus - the initial status to assign to the participant in the visit
     * @param patientId the CDR patient id or reference
     * @param providerId the CDR provider Id
     * @param encounterId the CDR encounter id or reference
     * @param encounterTenantKey the tenant key of the encounter. This takes priority over the tenant key in the token
     * @returns the participant that was created or updated including the context details
     */
    sendParticipantRequest(request) {
        var _a, _b, _c, _d;
        return __awaiter(this, void 0, void 0, function* () {
            const { url, token, conferenceVendorId, displayName, preferredPhone, fhirData, userAgent, patientId, providerId, encounterId, initialParticipantStatus, encounterTenantKey, proxySourceId, proxyDisplayName } = request;
            if (CoreUtils.isEmptyString(token)) {
                return Promise.reject(new Error('Internal Validation Error: Empty token'));
            }
            if (CoreUtils.isEmptyString(conferenceVendorId)) {
                return Promise.reject(new Error('Internal Validation Error: Empty conferenceVendorId'));
            }
            if (CoreUtils.isEmptyString(displayName)) {
                return Promise.reject(new Error('Internal Validation Error: Empty displayName'));
            }
            const phoneNumbers = ((_a = fhirData === null || fhirData === void 0 ? void 0 : fhirData.phoneNumbers) === null || _a === void 0 ? void 0 : _a.length) ? JSON.stringify(fhirData.phoneNumbers) : undefined;
            const emailAddresses = ((_b = fhirData === null || fhirData === void 0 ? void 0 : fhirData.emailAddresses) === null || _b === void 0 ? void 0 : _b.length) ? JSON.stringify(fhirData.emailAddresses) : undefined;
            const tokenContext = TokenContext.parse(token);
            const participantRequest = {
                conferenceVendorId,
                displayName,
                preferredPhone,
                phoneNumbers,
                emailAddresses,
                ehrId: tokenContext.payload.ehrId,
                participantSourceId: (_c = fhirData === null || fhirData === void 0 ? void 0 : fhirData.reference) !== null && _c !== void 0 ? _c : tokenContext.payload.participantSourceId,
                roomSourceId: tokenContext.payload.roomSourceId,
                tenantKey: encounterTenantKey !== null && encounterTenantKey !== void 0 ? encounterTenantKey : tokenContext.payload.tenantKey,
                status: initialParticipantStatus,
                externalUserId: (_d = fhirData === null || fhirData === void 0 ? void 0 : fhirData.externalUserId) !== null && _d !== void 0 ? _d : tokenContext.payload.externalUserId,
                userAgent,
                patientId,
                providerId,
                encounterId,
                proxySourceId,
                proxyDisplayName
            };
            console.debug('Setting up this participant for a visit in the VMS');
            return this.http.post(url, participantRequest, { headers: this.headers }).toPromise();
        });
    }
    /**
     * Validates and parses an Amwell token
     *
     * @param token the participant token
     *
     * @returns a TokenContext containing the details of the token in the header and payload.
     */
    parseToken(token) {
        console.debug('Started parseToken');
        if (CoreUtils.isEmptyString(token)) {
            return undefined;
        }
        const tokenContext = TokenContext.parse(token);
        console.debug('Leaving parseToken');
        return tokenContext;
    }
    /**
     * Generates a twilio voice token by sending a request to the VMS.
     * @param token - the token for the participant
     * @param conferenceId - the conference id for the participant
     * @param participantId - the id for the participant
     * @returns the twilio voice token that was generated
     */
    generateVoiceToken(token, conferenceId, participantId) {
        return __awaiter(this, void 0, void 0, function* () {
            console.debug('Starting generateVoiceToken');
            if (CoreUtils.isEmptyString(token)) {
                return Promise.reject(new Error('Internal Validation Error: Empty token'));
            }
            if (CoreUtils.isEmptyString(conferenceId)) {
                return Promise.reject(new Error('Internal Validation Error: Empty conferenceId'));
            }
            if (CoreUtils.isEmptyString(participantId)) {
                return Promise.reject(new Error('Internal Validation Error: Empty participantId'));
            }
            const url = CoreUtils.getEndpointUrl(this.serviceConfig.endpoints, 'generateVoiceToken');
            if (CoreUtils.isEmptyString(url)) {
                return Promise.reject(new Error('Internal Validation Error: No endpoint for generateVoiceToken found'));
            }
            const headers = new HttpHeaders({
                'Content-type': 'application/json',
                Authorization: `Bearer ${token}`,
            });
            const voiceTokenRequest = {
                conferenceId,
                participantId
            };
            console.debug('Generating voice token in VMS');
            return this.http.post(url + AppConstants.API_URL_VOICE_TOKEN, voiceTokenRequest, { headers })
                .toPromise()
                .catch((errorResponse) => {
                console.error('An error occurred while generating the voice token');
                return Promise.reject(errorResponse);
            });
        });
    }
}
VideoManagementClientService.ɵfac = function VideoManagementClientService_Factory(t) { return new (t || VideoManagementClientService)(ɵɵinject(HttpClient), ɵɵinject(ServiceConfig)); };
VideoManagementClientService.ɵprov = ɵɵdefineInjectable({ token: VideoManagementClientService, factory: VideoManagementClientService.ɵfac, providedIn: 'root' });
/*@__PURE__*/ (function () { ɵsetClassMetadata(VideoManagementClientService, [{
        type: Injectable,
        args: [{
                providedIn: 'root'
            }]
    }], function () { return [{ type: HttpClient }, { type: ServiceConfig, decorators: [{
                type: Inject,
                args: [ServiceConfig]
            }] }]; }, null); })();

class TokenSessionIdInterceptor {
    intercept(httpRequest, next) {
        return next.handle(this.fetchModifiedRequest(httpRequest));
    }
    /**
    * Fetches a modified HTTP request that includes the session Id, if available
    *
    * @param httpRequest the current http request
    * @returns the modified http request
    */
    fetchModifiedRequest(httpRequest) {
        return httpRequest.clone({
            headers: this.fetchModifiedHeaders(httpRequest.headers)
        });
    }
    /**
    * Fetches a modified HTTP headers that includes the session Id, if available
    *
    * @param headers the current http headers
    * @returns the modified http headers
    */
    fetchModifiedHeaders(httpHeaders) {
        const participantToken = SessionCacheService.getString(AppConstants.TOKEN_KEY);
        const sessionId = SessionCacheService.getString(AppConstants.SESSION_ID_KEY);
        //Set headers for token and sessionId
        if (!CoreUtils.isEmptyString(sessionId)) {
            httpHeaders = httpHeaders.set(AppConstants.SESSION_ID_HEADER_NAME, sessionId);
        }
        if (!CoreUtils.isEmptyString(participantToken)) {
            httpHeaders = httpHeaders.set(AppConstants.AUTH_TOKEN_HEADER_NAME, `${AppConstants.AUTH_TOKEN_PREFIX}${participantToken}`);
        }
        return httpHeaders;
    }
}
TokenSessionIdInterceptor.ɵfac = function TokenSessionIdInterceptor_Factory(t) { return new (t || TokenSessionIdInterceptor)(); };
TokenSessionIdInterceptor.ɵprov = ɵɵdefineInjectable({ token: TokenSessionIdInterceptor, factory: TokenSessionIdInterceptor.ɵfac });
/*@__PURE__*/ (function () { ɵsetClassMetadata(TokenSessionIdInterceptor, [{
        type: Injectable
    }], null, null); })();

/*!
 * American Well Converge
 *
 * Copyright © 2020 American Well.
 * All rights reserved.
 *
 * It is illegal to use, reproduce or distribute
 * any part of this Intellectual Property without
 * prior written authorization from American Well.
 */
class VideoManagementClientModule {
}
VideoManagementClientModule.ɵmod = ɵɵdefineNgModule({ type: VideoManagementClientModule });
VideoManagementClientModule.ɵinj = ɵɵdefineInjector({ factory: function VideoManagementClientModule_Factory(t) { return new (t || VideoManagementClientModule)(); }, providers: [
        SessionCacheService,
        {
            provide: HTTP_INTERCEPTORS,
            useClass: TokenSessionIdInterceptor,
            multi: true,
        },
    ] });
/*@__PURE__*/ (function () { ɵsetClassMetadata(VideoManagementClientModule, [{
        type: NgModule,
        args: [{
                providers: [
                    SessionCacheService,
                    {
                        provide: HTTP_INTERCEPTORS,
                        useClass: TokenSessionIdInterceptor,
                        multi: true,
                    },
                ],
            }]
    }], null, null); })();

class Participant {
    constructor() { }
}

/*!
 * American Well Converge
 *
 * Copyright © 2020 American Well.
 * All rights reserved.
 *
 * It is illegal to use, reproduce or distribute
 * any part of this Intellectual Property without
 * prior written authorization from American Well.
 */
class EncounterResponse extends Participant {
    constructor() { super(); }
}

/*
 * Public API Surface of video-management-client
 */

/**
 * Generated bundle index. Do not edit.
 */

export { EncounterResponse, IndividualFhirData, ParticipantStatus, ServiceConfig, TokenContext, VideoManagementClientModule, VideoManagementClientService };
//# sourceMappingURL=aw-vms-client.js.map
